import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, InfinityIcon, GiftIcon } from 'lucide-react';
import { GoldMedal } from './LifetimeSeal';
interface FreeUnitModalProps {
  open: boolean;
  /** Take the deal: switch the order to the 3-pack. */
  onAccept: () => void;
  /** Keep 2 single units at full price. */
  onDecline: () => void;
}

/**
 * Fires when someone tries to buy two single X1 at $79.90, which is the exact
 * price of the 3-pack. Rather than let them overpay, it offers the free third
 * unit and the lifetime coverage that comes with the multi-pack.
 */
export function FreeUnitModal({
  open,
  onAccept,
  onDecline
}: FreeUnitModalProps) {
  const acceptRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    acceptRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDecline();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onDecline]);
  return <AnimatePresence data-id="element-219" data-mp="axr6emqnnbib">
      {open && <motion.div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-ink-950/85 p-4 backdrop-blur-sm" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.16,
      ease: [0.23, 1, 0.32, 1]
    }} role="dialog" aria-modal="true" aria-labelledby="free-unit-heading" data-id="element-220" data-mp="aysv3u0lfwii">
          <motion.div className="relative my-auto w-full max-w-xl border-2 gold-edge bg-ink-950" initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-221" data-mp="3ndl9xam5i6i1">
            <button type="button" onClick={onDecline} aria-label="Close" className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center text-steel-400 transition-colors duration-150 ease-grit hover:text-offwhite focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-222" data-mp="34fqx0lhwjaud">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-223" data-mp="2lu2sz05tz2x2" />
            </button>

            <p className="gold-plate flex items-center gap-2 px-5 py-2.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-ink-950" data-id="element-224" data-mp="2pvedir9ukxye">
              <GiftIcon className="h-4 w-4" aria-hidden="true" data-id="element-225" data-mp="3iiipxhb9sek7" />
              You just unlocked a free X1
            </p>

            <div className="p-5 sm:p-7" data-id="element-226" data-mp="34psz8zksqlgr">
              <h2 id="free-unit-heading" className="grit-headline text-[clamp(2rem,7vw,3.25rem)] font-extrabold uppercase leading-[0.95] text-offwhite" data-id="element-227" data-mp="34orm8pk7zowe">
                Congratulations.
                <br data-id="element-228" data-mp="53ck8b2lhjxe" />
                <span className="gold-text" data-id="element-229" data-mp="53w4hyfpwkel">Your third X1 is free.</span>
              </h2>

              <p className="mt-4 text-[15px] leading-relaxed text-steel-200 sm:text-base" data-id="element-230" data-mp="1psgdfto3lpxx">
                Two systems cost <span className="font-bold text-offwhite" data-id="element-231" data-mp="1ktn9arupbbaf">$99.90</span>. Buy 2, Get 1 Free is{' '}
                <span className="font-bold text-grit-500" data-id="element-232" data-mp="1zhzwp0fq3ltb">$99.90 for three complete systems</span>, so for the same
                money you get a third X1 free, 9 lenses instead of 6, free U.S. shipping and the free mystery gift.
              </p>

              <div className="mt-5 flex items-center gap-4 border gold-edge bg-[#1a1408] p-4" data-id="element-233" data-mp="11vkf7ynaqo5r">
                <GoldMedal size={86} data-id="element-234" data-mp="1gstjtu1m1off" />
                <p className="font-display text-lg font-bold uppercase leading-tight tracking-wide text-[#ffe79a]" data-id="element-235" data-mp="1gtuvo0qgaey7">
                  All 3 covered for life.
                  <span className="mt-0.5 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-236" data-mp="2s7ypn6x1g339">
                    Lifetime Replacement Warranty on every X1 in the pack.
                  </span>
                </p>
              </div>

              <ul className="mt-5 grid gap-2 text-[15px] font-semibold text-steel-200" data-id="element-237" data-mp="wtcsnanbfrax">
                <li className="flex items-start gap-2.5" data-id="element-238" data-mp="11ro2hldm21t5">
                  <InfinityIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#f2c94c]" aria-hidden="true" data-id="element-239" data-mp="15sq3a6n6elx8" />
                  Same price as two. One extra complete system. Plus the mystery gift.
                </li>
              </ul>

              <button ref={acceptRef} type="button" onClick={onAccept} className="mt-6 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:text-2xl" data-id="element-240" data-mp="1fpl1spjg8mbi">
                Claim my free X1
                <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-241" data-mp="23e4av7abp34n" />
              </button>

              <button type="button" onClick={onDecline} className="mt-3 w-full px-4 py-3 text-center text-[13px] font-semibold text-steel-400 underline decoration-steel-600 underline-offset-4 transition-colors duration-150 ease-grit hover:text-offwhite focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-242" data-mp="2di2ie5nv8fk6">
                No thanks, I want 2 systems for $99.90 without the free one
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}